import React, { useState } from "react";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import { Mail, MapPin, Check } from "lucide-react";

const Contatti = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/contact", { ...form, lang });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Error");
    }
  };

  return (
    <div data-testid="contatti-page">
      <SEO
        titleIt="Contatti — Scrivi al Cammino Ibleo UNESCO"
        titleEn="Contact — Write to the Cammino Ibleo UNESCO"
        descriptionIt="Contatta il progetto Cammino Ibleo UNESCO: informazioni, collaborazioni istituzionali, richieste stampa e supporto per i camminatori."
        descriptionEn="Contact the Cammino Ibleo UNESCO project: information, institutional partnerships, press requests and hiker support."
      />
      <section className="pt-40 pb-16 bg-pietra-light border-b border-carrubo/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="eyebrow mb-6">Contatti</div>
          <h1 className="font-serif text-5xl md:text-7xl text-grafite mb-4">{t("contatti_title")}</h1>
          <p className="text-lg text-grafite-soft max-w-3xl">{t("contatti_subtitle")}</p>
        </div>
      </section>

      <section className="py-20 bg-pietra-light">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid md:grid-cols-[1fr_2fr] gap-16 items-start">
          <div className="space-y-6">
            <div className="flex items-start gap-3 text-grafite">
              <Mail size={20} className="text-carrubo mt-1" />
              <div>
                <div className="small-caps text-miele mb-1">Email</div>
                <a
                  href="mailto:alessandroveca0@gmail.com"
                  data-testid="contatti-email"
                  className="hover:text-carrubo transition-colors"
                >
                  alessandroveca0@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3 text-grafite">
              <MapPin size={20} className="text-carrubo mt-1" />
              <div>
                <div className="small-caps text-miele mb-1">Sede</div>
                Ragusa, Sicilia
              </div>
            </div>

            <div className="mt-10 p-6 bg-pietra border border-carrubo/15 rounded-sm">
              <div className="small-caps text-miele mb-2">Sicilia Orientale</div>
              <div className="text-grafite text-sm leading-relaxed">
                {lang === "it" ? "Un cammino ciclo-pedonale attraverso nove siti UNESCO." : "A cycle-pedestrian trail through nine UNESCO sites."}
              </div>
            </div>
          </div>

          {sent ? (
            <div data-testid="contact-success" className="p-16 bg-carrubo text-pietra-light rounded-sm text-center">
              <Check size={48} className="text-miele mx-auto mb-6" />
              <p className="font-serif text-3xl">{t("form_submit_success")}</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="space-y-6 bg-pietra p-10 rounded-sm border border-carrubo/15">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="small-caps text-grafite-soft mb-2 block">{t("form_fullname")}</label>
                  <input required data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-pietra-light border border-carrubo/20 focus:border-carrubo p-3 outline-none text-grafite rounded-sm" />
                </div>
                <div>
                  <label className="small-caps text-grafite-soft mb-2 block">{t("form_email")}</label>
                  <input required type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-pietra-light border border-carrubo/20 focus:border-carrubo p-3 outline-none text-grafite rounded-sm" />
                </div>
              </div>
              <div>
                <label className="small-caps text-grafite-soft mb-2 block">{t("form_subject")}</label>
                <input required data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full bg-pietra-light border border-carrubo/20 focus:border-carrubo p-3 outline-none text-grafite rounded-sm" />
              </div>
              <div>
                <label className="small-caps text-grafite-soft mb-2 block">{t("form_message")}</label>
                <textarea required rows={6} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-pietra-light border border-carrubo/20 focus:border-carrubo p-4 outline-none text-grafite rounded-sm" />
              </div>

              {error && <div className="text-red-600 text-sm">{error}</div>}

              <button
                type="submit"
                data-testid="contact-submit"
                className="w-full bg-carrubo hover:bg-carrubo-dark text-pietra-light font-bold py-4 rounded-sm tracking-widest uppercase text-sm"
              >
                {t("form_submit")}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Contatti;
